class SocialService {
  constructor() {
    this.storageKey = 'socialData';
    this.friendsKey = 'friendsList';
    this.achievementsKey = 'sharedAchievements';
    this.studySessionsKey = 'sharedStudySessions';
  }

  /**
   * Get social data
   */
  getSocialData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : this.getDefaultSocialData();
    } catch (error) {
      console.error('Error loading social data:', error);
      return this.getDefaultSocialData();
    }
  }

  /**
   * Get default social data structure
   */
  getDefaultSocialData() {
    return {
      friends: [],
      sharedAchievements: [],
      sharedStudySessions: [],
      studyGroups: [],
      challenges: [],
      notifications: {
        friendRequests: [],
        achievementShares: [],
        studyInvites: [],
        challengeInvites: []
      },
      privacy: {
        shareAchievements: true,
        shareStudySessions: true,
        shareProgress: true,
        allowFriendRequests: true
      }
    };
  }

  /**
   * Add a friend
   */
  addFriend(friendData) {
    const socialData = this.getSocialData();
    
    // Check if friend already exists
    const existingFriend = socialData.friends.find(f => f.id === friendData.id);
    if (existingFriend) {
      return { success: false, message: 'Friend already exists' };
    }
    
    const newFriend = {
      id: friendData.id,
      name: friendData.name,
      avatar: friendData.avatar || 'default-avatar.jpg',
      email: friendData.email,
      addedDate: new Date().toISOString(),
      studyStats: {
        currentStreak: 0,
        totalStudyDays: 0,
        achievements: 0
      },
      lastActive: new Date().toISOString()
    };
    
    socialData.friends.push(newFriend);
    this.saveSocialData(socialData);
    
    return { success: true, friend: newFriend };
  }

  /**
   * Remove a friend
   */
  removeFriend(friendId) {
    const socialData = this.getSocialData();
    socialData.friends = socialData.friends.filter(f => f.id !== friendId);
    this.saveSocialData(socialData);
    
    return { success: true };
  }

  /**
   * Share an achievement
   */
  shareAchievement(achievement, message = '') {
    const socialData = this.getSocialData();
    
    const sharedAchievement = {
      id: Date.now().toString(),
      achievement: achievement,
      message: message,
      sharedDate: new Date().toISOString(),
      likes: 0,
      comments: [],
      isPublic: true
    };
    
    socialData.sharedAchievements.unshift(sharedAchievement);
    this.saveSocialData(socialData);
    
    // Notify friends
    this.notifyFriends('achievement', {
      type: 'achievement_earned',
      achievement: achievement,
      message: message
    });
    
    return { success: true, sharedAchievement };
  }

  /**
   * Share a study session
   */
  shareStudySession(sessionData, message = '') {
    const socialData = this.getSocialData();
    
    const sharedSession = {
      id: Date.now().toString(),
      session: sessionData,
      message: message,
      sharedDate: new Date().toISOString(),
      likes: 0,
      comments: [],
      isPublic: true
    };
    
    socialData.sharedStudySessions.unshift(sharedSession);
    this.saveSocialData(socialData);
    
    // Notify friends
    this.notifyFriends('study_session', {
      type: 'study_session_completed',
      session: sessionData,
      message: message
    });
    
    return { success: true, sharedSession };
  }

  /**
   * Create a study group
   */
  createStudyGroup(groupData) {
    const socialData = this.getSocialData();
    
    const studyGroup = {
      id: Date.now().toString(),
      name: groupData.name,
      description: groupData.description,
      subjects: groupData.subjects || [],
      members: [groupData.creatorId],
      admins: [groupData.creatorId],
      createdDate: new Date().toISOString(),
      studySessions: [],
      challenges: [],
      isPrivate: groupData.isPrivate || false
    };
    
    socialData.studyGroups.push(studyGroup);
    this.saveSocialData(socialData);
    
    return { success: true, studyGroup };
  }

  /**
   * Join a study group
   */
  joinStudyGroup(groupId, userId) {
    const socialData = this.getSocialData();
    const group = socialData.studyGroups.find(g => g.id === groupId);
    
    if (!group) {
      return { success: false, message: 'Study group not found' };
    }
    
    if (group.members.includes(userId)) {
      return { success: false, message: 'Already a member' };
    }
    
    group.members.push(userId);
    this.saveSocialData(socialData);
    
    return { success: true, group };
  }

  /**
   * Create a study challenge
   */
  createChallenge(challengeData) {
    const socialData = this.getSocialData();
    
    const challenge = {
      id: Date.now().toString(),
      title: challengeData.title,
      description: challengeData.description,
      type: challengeData.type, // 'streak', 'hours', 'subjects', 'achievements'
      target: challengeData.target,
      duration: challengeData.duration, // in days
      participants: [challengeData.creatorId],
      leaderboard: [],
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + challengeData.duration * 24 * 60 * 60 * 1000).toISOString(),
      isActive: true,
      rewards: challengeData.rewards || {}
    };
    
    socialData.challenges.push(challenge);
    this.saveSocialData(socialData);
    
    return { success: true, challenge };
  }

  /**
   * Join a challenge
   */
  joinChallenge(challengeId, userId) {
    const socialData = this.getSocialData();
    const challenge = socialData.challenges.find(c => c.id === challengeId);
    
    if (!challenge) {
      return { success: false, message: 'Challenge not found' };
    }
    
    if (!challenge.isActive) {
      return { success: false, message: 'Challenge is not active' };
    }
    
    if (challenge.participants.includes(userId)) {
      return { success: false, message: 'Already participating' };
    }
    
    challenge.participants.push(userId);
    challenge.leaderboard.push({
      userId: userId,
      progress: 0,
      lastUpdated: new Date().toISOString()
    });
    
    this.saveSocialData(socialData);
    
    return { success: true, challenge };
  }

  /**
   * Update challenge progress
   */
  updateChallengeProgress(challengeId, userId, progress) {
    const socialData = this.getSocialData();
    const challenge = socialData.challenges.find(c => c.id === challengeId);
    
    if (!challenge) {
      return { success: false, message: 'Challenge not found' };
    }
    
    const participant = challenge.leaderboard.find(p => p.userId === userId);
    if (participant) {
      participant.progress = progress;
      participant.lastUpdated = new Date().toISOString();
    }
    
    // Sort leaderboard by progress
    challenge.leaderboard.sort((a, b) => b.progress - a.progress);
    
    this.saveSocialData(socialData);
    
    return { success: true, challenge };
  }

  /**
   * Get friend suggestions
   */
  getFriendSuggestions(currentUserId) {
    // Mock friend suggestions - in real app, this would query a database
    const suggestions = [
      {
        id: 'friend1',
        name: 'Alex Johnson',
        avatar: 'avatar1.jpg',
        email: 'alex@example.com',
        mutualFriends: 3,
        studyInterests: ['Mathematics', 'Physics'],
        lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 'friend2',
        name: 'Sarah Chen',
        avatar: 'avatar2.jpg',
        email: 'sarah@example.com',
        mutualFriends: 2,
        studyInterests: ['Chemistry', 'Biology'],
        lastActive: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 'friend3',
        name: 'Mike Rodriguez',
        avatar: 'avatar3.jpg',
        email: 'mike@example.com',
        mutualFriends: 1,
        studyInterests: ['History', 'Literature'],
        lastActive: new Date(Date.now() - 30 * 60 * 1000).toISOString()
      }
    ];
    
    return suggestions;
  }

  /**
   * Get study group suggestions
   */
  getStudyGroupSuggestions(userSubjects) {
    const socialData = this.getSocialData();
    
    return socialData.studyGroups
      .filter(group => !group.isPrivate)
      .filter(group => {
        if (group.subjects.length === 0) return true;
        return group.subjects.some(subject => userSubjects.includes(subject));
      })
      .map(group => ({
        ...group,
        memberCount: group.members.length,
        recentActivity: this.getGroupRecentActivity(group.id)
      }));
  }

  /**
   * Get active challenges
   */
  getActiveChallenges() {
    const socialData = this.getSocialData();
    const now = new Date();
    
    return socialData.challenges.filter(challenge => {
      const endDate = new Date(challenge.endDate);
      return challenge.isActive && endDate > now;
    });
  }

  /**
   * Get social feed
   */
  getSocialFeed(userId, limit = 20) {
    const socialData = this.getSocialData();
    const feed = [];
    
    // Add shared achievements
    socialData.sharedAchievements.forEach(achievement => {
      feed.push({
        type: 'achievement',
        data: achievement,
        timestamp: new Date(achievement.sharedDate)
      });
    });
    
    // Add shared study sessions
    socialData.sharedStudySessions.forEach(session => {
      feed.push({
        type: 'study_session',
        data: session,
        timestamp: new Date(session.sharedDate)
      });
    });
    
    // Add challenge updates
    socialData.challenges.forEach(challenge => {
      if (challenge.isActive) {
        feed.push({
          type: 'challenge_update',
          data: challenge,
          timestamp: new Date(challenge.startDate)
        });
      }
    });
    
    // Sort by timestamp and limit
    feed.sort((a, b) => b.timestamp - a.timestamp);
    return feed.slice(0, limit);
  }

  /**
   * Like a post
   */
  likePost(postId, postType, userId) {
    const socialData = this.getSocialData();
    
    let post;
    if (postType === 'achievement') {
      post = socialData.sharedAchievements.find(a => a.id === postId);
    } else if (postType === 'study_session') {
      post = socialData.sharedStudySessions.find(s => s.id === postId);
    }
    
    if (post) {
      post.likes += 1;
      this.saveSocialData(socialData);
      return { success: true, likes: post.likes };
    }
    
    return { success: false, message: 'Post not found' };
  }

  /**
   * Add comment to a post
   */
  addComment(postId, postType, userId, comment) {
    const socialData = this.getSocialData();
    
    let post;
    if (postType === 'achievement') {
      post = socialData.sharedAchievements.find(a => a.id === postId);
    } else if (postType === 'study_session') {
      post = socialData.sharedStudySessions.find(s => s.id === postId);
    }
    
    if (post) {
      const newComment = {
        id: Date.now().toString(),
        userId: userId,
        text: comment,
        timestamp: new Date().toISOString()
      };
      
      post.comments.push(newComment);
      this.saveSocialData(socialData);
      
      return { success: true, comment: newComment };
    }
    
    return { success: false, message: 'Post not found' };
  }

  /**
   * Get notifications
   */
  getNotifications(userId) {
    const socialData = this.getSocialData();
    const notifications = [];
    
    // Friend requests
    notifications.push(...socialData.notifications.friendRequests);
    
    // Achievement shares
    notifications.push(...socialData.notifications.achievementShares);
    
    // Study invites
    notifications.push(...socialData.notifications.studyInvites);
    
    // Challenge invites
    notifications.push(...socialData.notifications.challengeInvites);
    
    return notifications.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  /**
   * Mark notification as read
   */
  markNotificationAsRead(notificationId) {
    const socialData = this.getSocialData();
    
    // Mark as read in all notification arrays
    Object.keys(socialData.notifications).forEach(key => {
      socialData.notifications[key] = socialData.notifications[key].map(notification => {
        if (notification.id === notificationId) {
          return { ...notification, read: true };
        }
        return notification;
      });
    });
    
    this.saveSocialData(socialData);
    return { success: true };
  }

  /**
   * Update privacy settings
   */
  updatePrivacySettings(settings) {
    const socialData = this.getSocialData();
    socialData.privacy = { ...socialData.privacy, ...settings };
    this.saveSocialData(socialData);
    
    return { success: true, privacy: socialData.privacy };
  }

  /**
   * Notify friends about activity
   */
  notifyFriends(activityType, activityData) {
    const socialData = this.getSocialData();
    
    socialData.friends.forEach(friend => {
      const notification = {
        id: Date.now().toString() + Math.random(),
        type: activityType,
        data: activityData,
        timestamp: new Date().toISOString(),
        read: false,
        fromUserId: 'current_user', // This would be the actual user ID
        toUserId: friend.id
      };
      
      // Add to appropriate notification array
      if (activityType === 'achievement') {
        socialData.notifications.achievementShares.push(notification);
      } else if (activityType === 'study_session') {
        socialData.notifications.studyInvites.push(notification);
      }
    });
    
    this.saveSocialData(socialData);
  }

  /**
   * Get group recent activity
   */
  getGroupRecentActivity(groupId) {
    const socialData = this.getSocialData();
    const group = socialData.studyGroups.find(g => g.id === groupId);
    
    if (!group) return [];
    
    // Return last 5 study sessions
    return group.studySessions.slice(-5);
  }

  /**
   * Save social data
   */
  saveSocialData(socialData) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(socialData));
    } catch (error) {
      console.error('Error saving social data:', error);
    }
  }

  /**
   * Export social data for sharing
   */
  exportSocialData() {
    const socialData = this.getSocialData();
    return {
      achievements: socialData.sharedAchievements,
      studySessions: socialData.sharedStudySessions,
      friends: socialData.friends.length,
      studyGroups: socialData.studyGroups.length,
      challenges: socialData.challenges.length
    };
  }

  /**
   * Reset social data (for testing)
   */
  resetSocialData() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.friendsKey);
    localStorage.removeItem(this.achievementsKey);
    localStorage.removeItem(this.studySessionsKey);
  }
}

export default new SocialService(); 